import React, { useRef, useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';

// ============================================
// STYLED COMPONENTS
// ============================================

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(23, 36, 62, 0.49);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(4px);
`;

const Modal = styled.div`
  background: #f3f5f9;
  border-radius: 16px;
  width: 95%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(39, 68, 131, 0.105);
  border: 1px solid #dce5f4;
`;

const Header = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid #e0e8f5;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #192947;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #65748d;
  padding: 0;
  line-height: 1;
  
  &:hover {
    color: #192947;
  }
`;

const Content = styled.div`
  padding: 24px;
`;

const PurposeBox = styled.div`
  background: rgba(224, 85, 155, 0.15);
  border: 1px solid rgba(224, 85, 155, 0.3);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 24px;
`;

const PurposeLabel = styled.div`
  font-size: 12px;
  color: #65748d;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const PurposeText = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #c8367f;
`;

const Description = styled.div`
  font-size: 14px;
  color: #455776;
  margin-top: 8px;
  line-height: 1.4;
`;

const SectionTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #192947;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  .required {
    color: #d63c3c;
    font-size: 12px;
  }
  
  .check {
    color: #0f8f63;
    font-size: 16px;
  }
`;

const SectionDivider = styled.div`
  margin: 20px 0;
  border-top: 1px solid #e0e8f5;
`;

const CanvasContainer = styled.div`
  border: 2px dashed #cad5e9;
  border-radius: 12px;
  overflow: hidden;
  background: #f2f5fa;
  position: relative;
  margin-bottom: 12px;
`;

const Canvas = styled.canvas`
  display: block;
  width: 100%;
  touch-action: none;
  cursor: crosshair;
`;

const CanvasPlaceholder = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #a9b5c8;
  font-size: 14px;
  pointer-events: none;
  text-align: center;
`;

const ClearButton = styled.button`
  background: #edf2ff;
  border: 1px solid #dce5f4;
  color: #455776;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  
  &:hover {
    background: #edf2ff;
  }
`;

const TypedInput = styled.input`
  width: 100%;
  padding: 16px;
  font-size: 28px;
  font-family: 'Brush Script MT', 'Segoe Script', cursive;
  border: 2px solid #dce5f4;
  border-radius: 12px;
  background: #f2f5fa;
  color: #192947;
  text-align: center;
  margin-bottom: 16px;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #e0559b;
  }
  
  &::placeholder {
    color: #a9b5c8;
  }
`;

const TypedPreview = styled.div`
  background: #f2f5fa;
  border: 2px dashed #cad5e9;
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  font-family: 'Brush Script MT', 'Segoe Script', cursive;
  font-size: 36px;
  color: #192947;
  min-height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
`;

const ConsentBox = styled.div`
  background: rgba(51, 92, 244, 0.1);
  border: 1px solid rgba(51, 92, 244, 0.3);
  border-radius: 12px;
  padding: 16px;
  margin: 24px 0;
`;

const ConsentLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  font-size: 13px;
  color: #33445e;
  line-height: 1.5;
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  margin-top: 2px;
  cursor: pointer;
  accent-color: #c8367f;
`;

const Footer = styled.div`
  padding: 20px 24px;
  border-top: 1px solid #e0e8f5;
  display: flex;
  justify-content: space-between;
  gap: 12px;
`;

const Button = styled.button`
  flex: 1;
  padding: 14px 24px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
`;

const CancelButtonStyled = styled(Button)`
  background: #edf2ff;
  border: 1px solid #dce5f4;
  color: #455776;
  
  &:hover {
    background: #edf2ff;
  }
`;

const SubmitButtonStyled = styled(Button)`
  background: linear-gradient(135deg, #e0559b 0%, #c8367f 100%);
  border: none;
  color: white;
  
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(224, 85, 155, 0.4);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DeclineButtonStyled = styled(Button)`
  background: transparent;
  border: 1px solid rgba(229, 72, 77, 0.5);
  color: #d63c3c;
  
  &:hover {
    background: rgba(229, 72, 77, 0.1);
  }
`;

// ============================================
// COMPONENT
// ============================================

const SignaturePad = ({
  isOpen,
  onClose,
  onSubmit,
  onDecline,
  purpose,
  description,
  requestCode,
  isSubmitting = false
}) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [typedName, setTypedName] = useState('');
  const [consentGiven, setConsentGiven] = useState(false);
  const [paths, setPaths] = useState([]);
  const [currentPath, setCurrentPath] = useState([]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setPaths([]);
      setCurrentPath([]);
      setHasDrawn(false);
      setTypedName('');
      setConsentGiven(false);
    }
  }, [isOpen]);

  // Initialize canvas
  useEffect(() => {
    if (isOpen && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const rect = canvas.getBoundingClientRect();
      
      // Set canvas size for high DPI
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = 150 * dpr;
      canvas.style.height = '150px';
      ctx.scale(dpr, dpr);
      
      // Set drawing style
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  }, [isOpen]);

  // Redraw all paths when they change
  useEffect(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const rect = canvas.getBoundingClientRect();
      
      // Clear canvas
      ctx.clearRect(0, 0, rect.width, 150);
      
      // Redraw all paths
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      [...paths, currentPath].forEach(path => {
        if (path.length > 0) {
          ctx.beginPath();
          ctx.moveTo(path[0].x, path[0].y);
          path.forEach(point => {
            ctx.lineTo(point.x, point.y);
          });
          ctx.stroke();
        }
      });
    }
  }, [paths, currentPath]);

  const getPointerPosition = useCallback((e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  }, []);

  const handlePointerDown = useCallback((e) => {
    e.preventDefault();
    setIsDrawing(true);
    const pos = getPointerPosition(e);
    setCurrentPath([pos]);
  }, [getPointerPosition]);

  const handlePointerMove = useCallback((e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const pos = getPointerPosition(e);
    setCurrentPath(prev => [...prev, pos]);
    setHasDrawn(true);
  }, [isDrawing, getPointerPosition]);

  const handlePointerUp = useCallback(() => {
    if (isDrawing && currentPath.length > 0) {
      setPaths(prev => [...prev, currentPath]);
      setCurrentPath([]);
    }
    setIsDrawing(false);
  }, [isDrawing, currentPath]);

  const clearCanvas = () => {
    setPaths([]);
    setCurrentPath([]);
    setHasDrawn(false);
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const rect = canvas.getBoundingClientRect();
      ctx.clearRect(0, 0, rect.width, 150);
    }
  };

  const generateSVG = () => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    let pathData = '';
    paths.forEach(path => {
      if (path.length > 0) {
        pathData += `M ${path[0].x} ${path[0].y} `;
        path.slice(1).forEach(point => {
          pathData += `L ${point.x} ${point.y} `;
        });
      }
    });
    
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${rect.width}" height="150" viewBox="0 0 ${rect.width} 150">
      <path d="${pathData}" stroke="#000000" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`;
  };

  const handleSubmit = () => {
    // Require both drawn signature AND typed name
    if (!consentGiven || !hasDrawn || !typedName.trim()) return;

    const signatureData = {
      requestCode,
      drawnSignature: {
        svg: generateSVG(),
        width: canvasRef.current?.getBoundingClientRect().width || 400,
        height: 150,
        strokeColor: '#000000'
      },
      typedSignature: {
        text: typedName.trim(),
        fontFamily: 'Brush Script MT, cursive',
        fontSize: 36
      },
      consentGiven: true,
      consentText: 'I agree to use this as my legal digital signature.',
      metadata: {
        userAgent: navigator.userAgent,
        deviceType: /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
        screenResolution: `${window.screen.width}x${window.screen.height}`
      }
    };

    onSubmit(signatureData);
  };

  // Require BOTH drawn signature AND typed name
  const isValid = consentGiven && hasDrawn && typedName.trim();

  if (!isOpen) return null;

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()}>
        <Header>
          <Title>✍️ Provide Your Signature</Title>
          <CloseButton onClick={onClose}>×</CloseButton>
        </Header>
        
        <Content>
          <PurposeBox>
            <PurposeLabel>Signature Required For</PurposeLabel>
            <PurposeText>{purpose}</PurposeText>
            {description && (
              <Description>{description}</Description>
            )}
          </PurposeBox>
          
          {/* Section 1: Draw Signature */}
          <SectionTitle>
            ✏️ Draw Your Signature <span className="required">*</span>
            {hasDrawn && <span className="check">✓</span>}
          </SectionTitle>
          <CanvasContainer>
            <Canvas
              ref={canvasRef}
              onMouseDown={handlePointerDown}
              onMouseMove={handlePointerMove}
              onMouseUp={handlePointerUp}
              onMouseLeave={handlePointerUp}
              onTouchStart={handlePointerDown}
              onTouchMove={handlePointerMove}
              onTouchEnd={handlePointerUp}
            />
            {!hasDrawn && (
              <CanvasPlaceholder>
                Draw your signature here
              </CanvasPlaceholder>
            )}
          </CanvasContainer>
          <ClearButton onClick={clearCanvas}>
            🗑️ Clear
          </ClearButton>
          
          <SectionDivider />
          
          {/* Section 2: Type Full Name */}
          <SectionTitle>
            ⌨️ Type Your Full Name <span className="required">*</span>
            {typedName.trim() && <span className="check">✓</span>}
          </SectionTitle>
          <TypedInput
            type="text"
            placeholder="Type your full legal name"
            value={typedName}
            onChange={(e) => setTypedName(e.target.value)}
          />
          <TypedPreview>
            {typedName || 'Your typed signature preview'}
          </TypedPreview>
          
          <ConsentBox>
            <ConsentLabel>
              <Checkbox 
                type="checkbox" 
                checked={consentGiven}
                onChange={(e) => setConsentGiven(e.target.checked)}
              />
              <span>
                I agree that this digital signature is legally binding and represents my intent to sign 
                the document(s) for the purpose stated above. I understand this signature will be stored 
                securely and may be used for official documents.
              </span>
            </ConsentLabel>
          </ConsentBox>
        </Content>
        
        <Footer>
          <DeclineButtonStyled onClick={onDecline}>
            Decline
          </DeclineButtonStyled>
          <SubmitButtonStyled 
            disabled={!isValid || isSubmitting}
            onClick={handleSubmit}
          >
            {isSubmitting ? 'Submitting...' : '✍️ Submit Signature'}
          </SubmitButtonStyled>
        </Footer>
      </Modal>
    </Overlay>
  );
};

export default SignaturePad;
